import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const MY_DEFAULTS = { field_size_min: 8, field_size_max: 11, track_rating_min: 1, track_rating_max: 5, no_resumers: true, score_all: false };

const Toggle2 = ({ label, value, onChange, a, b, testId }) => (
  <div>
    <div className="text-xs text-slate-400 mb-1.5">{label}</div>
    <div className="inline-flex bg-[#0F172A] border border-slate-800 rounded p-0.5">
      {[[a[0], a[1]], [b[0], b[1]]].map(([v, l]) => (
        <button key={l} type="button" data-testid={`${testId}-${v ? "on" : "off"}`} onClick={() => value !== v && onChange(v)}
          className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${value === v ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  </div>
);

const MarketPct = ({ settings, setSettings, onSaved }) => {
  const [v, setV] = useState(settings.formula.market_pct ?? 100);
  const save = async (val = v) => {
    const n = Number(val) || 100;
    if (n === (settings.formula.market_pct ?? 100)) return;
    try {
      const { data } = await api.put("/settings", { ...settings, formula: { ...settings.formula, market_pct: n } });
      setSettings(data);
      toast.success(`Market set to ${n}%`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="text-xs text-slate-400 mb-1.5">Market % (100% = straight 100 ÷ % chance prices)</div>
      <div className="flex items-center gap-2">
        <input type="number" step="1" min="50" max="200" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => save()} data-testid="option-market-pct"
          className="h-9 w-24 bg-[#0F172A] border border-slate-700 rounded px-2 text-sm num text-white focus:outline-none focus:border-emerald-500" />
        <span className="text-xs text-slate-500">%</span>
        {Number(v) !== 100 && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setV(100); save(100); }} className="text-[11px] font-mono text-amber-300" data-testid="option-market-reset">Reset to 100%</button>}
      </div>
    </div>
  );
};

export const SelectionOptions = ({ onChange, embedded = false }) => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const apply = async (rules) => {
    try {
      const { data } = await api.put("/settings", { ...settings, rules: { ...settings.rules, ...rules } });
      setSettings(data);
      toast.success("Selection options saved");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!settings) return null;
  const r = settings.rules;
  const isDefault = Object.entries(MY_DEFAULTS).every(([k, v]) => r[k] === v);
  return (
    <div className="space-y-4" data-testid="selection-options">
      <div className={`flex items-center gap-2 ${embedded ? "justify-start" : "justify-between"}`}>
        {embedded ? null : <div className="eyebrow"><span className="text-emerald-400 num mr-1.5">1</span>Selection options</div>}
        {r.score_all ? null : isDefault
          ? <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="option-default-badge">My defaults</span>
          : <button type="button" onClick={() => apply(MY_DEFAULTS)} data-testid="option-apply-defaults" className="text-[11px] font-mono text-amber-300 hover:text-amber-200 transition-colors">Use my default rules</button>}
      </div>
      <Toggle2 label="Races to score" testId="option-score-all" value={!!r.score_all} onChange={(v) => apply({ score_all: v })}
        a={[false, "Preferred Betting Races only"]} b={[true, "All races"]} />
      {!r.score_all && (
        <p className="text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded p-2.5" data-testid="option-preferred-note">
          These races are assessed and rated by Strategic Ratings&apos; exclusive data and form formulas.
        </p>
      )}
      {r.score_all && (
        <p className="text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded p-2.5" data-testid="option-all-races-note">
          This feature rates all horses in every race. First starters with no exposed form are listed at the end of each field with a 0.0 rating and $0.00 price. Allowances for track conditions and those 1st and 2nd up have been factored as part of the horse&apos;s final rating.
        </p>
      )}
      <MarketPct settings={settings} setSettings={setSettings} onSaved={onChange} />
      <div className="flex items-start gap-2 text-xs text-slate-400 bg-[#0F172A] border border-slate-800 rounded p-2.5" data-testid="option-first-starters-locked">
        <Lock className="h-3.5 w-3.5 mt-0.5 text-amber-400 shrink-0" />
        <span>Races with first starters are never Preferred Betting Races. First starters are never rated; they are listed under "First Starters - No Exposed Form".{r.score_all ? " In All races mode these races are still scored for every horse with form." : ""}</span>
      </div>
    </div>
  );
};
